import { getTerritoriesByRegion } from '~/lib/territories/utils/getTerritoriesByRegion';

export interface Territory {
  code: string;
  region: string;
  subregion: string;
  // NOTE: Country names are translated which is why they are not part of the data here.
}

export type TerritoryRegion = Record<string, Territory[]>;

// -----------------------------------------------------------------------------
//
// NOTE: This is our "source of truth" for territories used in WMG1. We keep it
//       keyed on territory code so that we can define the TerritoryCode type,
//       which allows us to make sure that anytime we are expecting a territory
//       code that it's a valid one (enforced by TypeScript).
//
// -----------------------------------------------------------------------------
export const TERRITORIES: Record<string, Territory> = {
  AD: {
    code: 'AD', // Andorra
    region: 'Europe',
    subregion: 'Iberia',
  },
  AE: {
    code: 'AE', // United Arab Emirates
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  AF: {
    code: 'AF', // Afghanistan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  AG: {
    code: 'AG', // Antigua and Barbuda
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  AI: {
    code: 'AI', // Anguilla
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  AL: {
    code: 'AL', // Albania
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  AM: {
    code: 'AM', // Armenia
    region: 'Emerging Markets',
    subregion: 'Caucasus',
  },
  AN: {
    code: 'AN', // Netherlands Antilles
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  AO: {
    code: 'AO', // Angola
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  AQ: {
    code: 'AQ', // Antarctica
    region: 'Emerging Markets',
    subregion: 'Antarctica',
  },
  AR: {
    code: 'AR', // Argentina
    region: 'LATAM',
    subregion: 'South America',
  },
  AS: {
    code: 'AS', // American Samoa
    region: 'North America',
    subregion: 'United States',
  },
  AT: {
    code: 'AT', // Austria
    region: 'Europe',
    subregion: 'GSA',
  },
  AU: {
    code: 'AU', // Australia
    region: 'APAC',
    subregion: 'Australia & New Zealand',
  },
  AW: {
    code: 'AW', // Aruba
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  AX: {
    code: 'AX', // Åland Islands
    region: 'Europe',
    subregion: 'Nordics',
  },
  AZ: {
    code: 'AZ', // Azerbaijan
    region: 'Emerging Markets',
    subregion: 'Caucasus',
  },
  BA: {
    code: 'BA', // Bosnia and Herzegovina
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  BB: {
    code: 'BB', // Barbados
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  BD: {
    code: 'BD', // Bangladesh
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  BE: {
    code: 'BE', // Belgium
    region: 'Europe',
    subregion: 'Benelux',
  },
  BF: {
    code: 'BF', // Burkina Faso
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  BG: {
    code: 'BG', // Bulgaria
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  BH: {
    code: 'BH', // Bahrain
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  BI: {
    code: 'BI', // Burundi
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  BJ: {
    code: 'BJ', // Benin
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  BL: {
    code: 'BL', // Saint Barthélemy
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  BM: {
    code: 'BM', // Bermuda
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  BN: {
    code: 'BN', // Brunei Darussalam
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  BO: {
    code: 'BO', // Bolivia (Plurinational State of)
    region: 'LATAM',
    subregion: 'South America',
  },
  BQ: {
    code: 'BQ', // Bonaire, Sint Eustatius and Saba
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  BR: {
    code: 'BR', // Brazil
    region: 'LATAM',
    subregion: 'Brazil',
  },
  BS: {
    code: 'BS', // Bahamas
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  BT: {
    code: 'BT', // Bhutan
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  BV: {
    code: 'BV', // Bouvet Island
    region: 'Emerging Markets',
    subregion: 'Antarctica',
  },
  BW: {
    code: 'BW', // Botswana
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  BY: {
    code: 'BY', // Belarus
    region: 'Emerging Markets',
    subregion: 'Eastern Europe',
  },
  BZ: {
    code: 'BZ', // Belize
    region: 'LATAM',
    subregion: 'Central America',
  },
  CA: {
    code: 'CA', // Canada
    region: 'North America',
    subregion: 'Canada',
  },
  CC: {
    code: 'CC', // Cocos (Keeling) Islands
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  CD: {
    code: 'CD', // Congo (the Democratic Republic of the)
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  CF: {
    code: 'CF', // Central African Republic
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  CG: {
    code: 'CG', // Congo
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  CH: {
    code: 'CH', // Switzerland
    region: 'Europe',
    subregion: 'GSA',
  },
  CI: {
    code: 'CI', // Côte d'Ivoire
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  CK: {
    code: 'CK', // Cook Islands
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  CL: {
    code: 'CL', // Chile
    region: 'LATAM',
    subregion: 'South America',
  },
  CM: {
    code: 'CM', // Cameroon
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  CN: {
    code: 'CN', // China
    region: 'APAC',
    subregion: 'Greater China',
  },
  CO: {
    code: 'CO', // Colombia
    region: 'LATAM',
    subregion: 'South America',
  },
  CR: {
    code: 'CR', // Costa Rica
    region: 'LATAM',
    subregion: 'Central America',
  },
  CS: {
    code: 'CS', // Serbia and Montenegro
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  CU: {
    code: 'CU', // Cuba
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  CV: {
    code: 'CV', // Cabo Verde
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  CW: {
    code: 'CW', // Curaçao
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  CX: {
    code: 'CX', // Christmas Island
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  CY: {
    code: 'CY', // Cyprus
    region: 'Emerging Markets',
    subregion: 'Eastern Mediterranean',
  },
  CZ: {
    code: 'CZ', // Czechia
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  DE: {
    code: 'DE', // Germany
    region: 'Europe',
    subregion: 'GSA',
  },
  DJ: {
    code: 'DJ', // Djibouti
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  DK: {
    code: 'DK', // Denmark
    region: 'Europe',
    subregion: 'Nordics',
  },
  DM: {
    code: 'DM', // Dominica
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  DO: {
    code: 'DO', // Dominican Republic
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  DZ: {
    code: 'DZ', // Algeria
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  EC: {
    code: 'EC', // Ecuador
    region: 'LATAM',
    subregion: 'South America',
  },
  EE: {
    code: 'EE', // Estonia
    region: 'Europe',
    subregion: 'Nordics',
  },
  EG: {
    code: 'EG', // Egypt
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  EH: {
    code: 'EH', // Western Sahara
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  ER: {
    code: 'ER', // Eritrea
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  ES: {
    code: 'ES', // Spain
    region: 'Europe',
    subregion: 'Iberia',
  },
  ET: {
    code: 'ET', // Ethiopia
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  FI: {
    code: 'FI', // Finland
    region: 'Europe',
    subregion: 'Nordics',
  },
  FJ: {
    code: 'FJ', // Fiji
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  FK: {
    code: 'FK', // Falkland Islands (Malvinas)
    region: 'LATAM',
    subregion: 'South America',
  },
  FM: {
    code: 'FM', // Micronesia (Federated States of)
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  FO: {
    code: 'FO', // Faroe Islands
    region: 'Europe',
    subregion: 'Nordics',
  },
  FR: {
    code: 'FR', // France
    region: 'Europe',
    subregion: 'France',
  },
  GA: {
    code: 'GA', // Gabon
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  GB: {
    code: 'GB', // United Kingdom of Great Britain and Northern Ireland
    region: 'Europe',
    subregion: 'Britain & Ireland',
  },
  GD: {
    code: 'GD', // Grenada
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  GE: {
    code: 'GE', // Georgia
    region: 'Emerging Markets',
    subregion: 'Caucasus',
  },
  GF: {
    code: 'GF', // French Guiana
    region: 'LATAM',
    subregion: 'South America',
  },
  GG: {
    code: 'GG', // Guernsey
    region: 'Europe',
    subregion: 'Britain & Ireland',
  },
  GH: {
    code: 'GH', // Ghana
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  GI: {
    code: 'GI', // Gibraltar
    region: 'Europe',
    subregion: 'Iberia',
  },
  GL: {
    code: 'GL', // Greenland
    region: 'Europe',
    subregion: 'Nordics',
  },
  GM: {
    code: 'GM', // Gambia
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  GN: {
    code: 'GN', // Guinea
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  GP: {
    code: 'GP', // Guadeloupe
    region: 'Europe',
    subregion: 'France',
  },
  GQ: {
    code: 'GQ', // Equatorial Guinea
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  GR: {
    code: 'GR', // Greece
    region: 'Emerging Markets',
    subregion: 'Eastern Mediterranean',
  },
  GS: {
    code: 'GS', // South Georgia and the South Sandwich Islands
    region: 'LATAM',
    subregion: 'South America',
  },
  GT: {
    code: 'GT', // Guatemala
    region: 'LATAM',
    subregion: 'Central America',
  },
  GU: {
    code: 'GU', // Guam
    region: 'North America',
    subregion: 'United States',
  },
  GW: {
    code: 'GW', // Guinea-Bissau
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  GY: {
    code: 'GY', // Guyana
    region: 'LATAM',
    subregion: 'South America',
  },
  HK: {
    code: 'HK', // Hong Kong
    region: 'APAC',
    subregion: 'Greater China',
  },
  HM: {
    code: 'HM', // Heard Island and McDonald Islands
    region: 'APAC',
    subregion: 'Australia & New Zealand',
  },
  HN: {
    code: 'HN', // Honduras
    region: 'LATAM',
    subregion: 'Central America',
  },
  HR: {
    code: 'HR', // Croatia
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  HT: {
    code: 'HT', // Haiti
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  HU: {
    code: 'HU', // Hungary
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  ID: {
    code: 'ID', // Indonesia
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  IE: {
    code: 'IE', // Ireland
    region: 'Europe',
    subregion: 'Britain & Ireland',
  },
  IL: {
    code: 'IL', // Israel
    region: 'Emerging Markets',
    subregion: 'Eastern Mediterranean',
  },
  IM: {
    code: 'IM', // Isle of Man
    region: 'Europe',
    subregion: 'Britain & Ireland',
  },
  IN: {
    code: 'IN', // India
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  IO: {
    code: 'IO', // British Indian Ocean Territory
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  IQ: {
    code: 'IQ', // Iraq
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  IR: {
    code: 'IR', // Iran (Islamic Republic of)
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  IS: {
    code: 'IS', // Iceland
    region: 'Europe',
    subregion: 'Nordics',
  },
  IT: {
    code: 'IT', // Italy
    region: 'Europe',
    subregion: 'Italian Peninsula',
  },
  JE: {
    code: 'JE', // Jersey
    region: 'Europe',
    subregion: 'Britain & Ireland',
  },
  JM: {
    code: 'JM', // Jamaica
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  JO: {
    code: 'JO', // Jordan
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  JP: {
    code: 'JP', // Japan
    region: 'APAC',
    subregion: 'Japan',
  },
  KE: {
    code: 'KE', // Kenya
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  KG: {
    code: 'KG', // Kyrgyzstan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  KH: {
    code: 'KH', // Cambodia
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  KI: {
    code: 'KI', // Kiribati
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  KM: {
    code: 'KM', // Comoros
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  KN: {
    code: 'KN', // Saint Kitts and Nevis
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  KP: {
    code: 'KP', // Korea (the Democratic People's Republic of)
    region: 'APAC',
    subregion: 'East Asia',
  },
  KR: {
    code: 'KR', // Korea (the Republic of)
    region: 'APAC',
    subregion: 'Korea',
  },
  KW: {
    code: 'KW', // Kuwait
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  KY: {
    code: 'KY', // Cayman Islands
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  KZ: {
    code: 'KZ', // Kazakhstan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  LA: {
    code: 'LA', // Lao People's Democratic Republic
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  LB: {
    code: 'LB', // Lebanon
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  LC: {
    code: 'LC', // Saint Lucia
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  LI: {
    code: 'LI', // Liechtenstein
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  LK: {
    code: 'LK', // Sri Lanka
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  LR: {
    code: 'LR', // Liberia
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  LS: {
    code: 'LS', // Lesotho
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  LT: {
    code: 'LT', // Lithuania
    region: 'Europe',
    subregion: 'Nordics',
  },
  LU: {
    code: 'LU', // Luxembourg
    region: 'Europe',
    subregion: 'Benelux',
  },
  LV: {
    code: 'LV', // Latvia
    region: 'Europe',
    subregion: 'Nordics',
  },
  LY: {
    code: 'LY', // Libya
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  MA: {
    code: 'MA', // Morocco
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  MC: {
    code: 'MC', // Monaco
    region: 'Europe',
    subregion: 'France',
  },
  MD: {
    code: 'MD', // Moldova (the Republic of)
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  ME: {
    code: 'ME', // Montenegro
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  MF: {
    code: 'MF', // Saint Martin (French part)
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  MG: {
    code: 'MG', // Madagascar
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  MH: {
    code: 'MH', // Marshall Islands
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  MK: {
    code: 'MK', // North Macedonia
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  ML: {
    code: 'ML', // Mali
    region: 'Emerging Markets',
    subregion: 'Sahel',
  },
  MM: {
    code: 'MM', // Myanmar
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  MN: {
    code: 'MN', // Mongolia
    region: 'APAC',
    subregion: 'East Asia',
  },
  MO: {
    code: 'MO', // Macao
    region: 'APAC',
    subregion: 'Greater China',
  },
  MP: {
    code: 'MP', // Northern Mariana Islands
    region: 'North America',
    subregion: 'United States',
  },
  MQ: {
    code: 'MQ', // Martinique
    region: 'Europe',
    subregion: 'France',
  },
  MR: {
    code: 'MR', // Mauritania
    region: 'Emerging Markets',
    subregion: 'Sahel',
  },
  MS: {
    code: 'MS', // Montserrat
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  MT: {
    code: 'MT', // Malta
    region: 'Europe',
    subregion: 'Italian Peninsula',
  },
  MU: {
    code: 'MU', // Mauritius
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  MV: {
    code: 'MV', // Maldives
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  MW: {
    code: 'MW', // Malawi
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  MX: {
    code: 'MX', // Mexico
    region: 'LATAM',
    subregion: 'Mexico',
  },
  MY: {
    code: 'MY', // Malaysia
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  MZ: {
    code: 'MZ', // Mozambique
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  NA: {
    code: 'NA', // Namibia
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  NC: {
    code: 'NC', // New Caledonia
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  NE: {
    code: 'NE', // Niger
    region: 'Emerging Markets',
    subregion: 'Sahel',
  },
  NF: {
    code: 'NF', // Norfolk Island
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  NG: {
    code: 'NG', // Nigeria
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  NI: {
    code: 'NI', // Nicaragua
    region: 'LATAM',
    subregion: 'Central America',
  },
  NL: {
    code: 'NL', // Netherlands
    region: 'Europe',
    subregion: 'Benelux',
  },
  NO: {
    code: 'NO', // Norway
    region: 'Europe',
    subregion: 'Nordics',
  },
  NP: {
    code: 'NP', // Nepal
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  NR: {
    code: 'NR', // Nauru
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  NU: {
    code: 'NU', // Niue
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  NZ: {
    code: 'NZ', // New Zealand
    region: 'APAC',
    subregion: 'Australia & New Zealand',
  },
  OM: {
    code: 'OM', // Oman
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  PA: {
    code: 'PA', // Panama
    region: 'LATAM',
    subregion: 'Central America',
  },
  PE: {
    code: 'PE', // Peru
    region: 'LATAM',
    subregion: 'South America',
  },
  PF: {
    code: 'PF', // French Polynesia
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  PG: {
    code: 'PG', // Papua New Guinea
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  PH: {
    code: 'PH', // Philippines
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  PK: {
    code: 'PK', // Pakistan
    region: 'Emerging Markets',
    subregion: 'South Asia',
  },
  PL: {
    code: 'PL', // Poland
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  PM: {
    code: 'PM', // Saint Pierre and Miquelon
    region: 'Europe',
    subregion: 'France',
  },
  PN: {
    code: 'PN', // Pitcairn
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  PR: {
    code: 'PR', // Puerto Rico
    region: 'North America',
    subregion: 'United States',
  },
  PS: {
    code: 'PS', // Palestine (State of)
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  PT: {
    code: 'PT', // Portugal
    region: 'Europe',
    subregion: 'Iberia',
  },
  PW: {
    code: 'PW', // Palau
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  PY: {
    code: 'PY', // Paraguay
    region: 'LATAM',
    subregion: 'South America',
  },
  QA: {
    code: 'QA', // Qatar
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  RE: {
    code: 'RE', // Réunion
    region: 'Europe',
    subregion: 'France',
  },
  RO: {
    code: 'RO', // Romania
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  RS: {
    code: 'RS', // Serbia
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  RU: {
    code: 'RU', // Russian Federation
    region: 'Emerging Markets',
    subregion: 'Eastern Europe',
  },
  RW: {
    code: 'RW', // Rwanda
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  SA: {
    code: 'SA', // Saudi Arabia
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  SB: {
    code: 'SB', // Solomon Islands
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  SC: {
    code: 'SC', // Seychelles
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  SD: {
    code: 'SD', // Sudan
    region: 'Emerging Markets',
    subregion: 'Sahel',
  },
  SE: {
    code: 'SE', // Sweden
    region: 'Europe',
    subregion: 'Nordics',
  },
  SG: {
    code: 'SG', // Singapore
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  SH: {
    code: 'SH', // Saint Helena, Ascension and Tristan da Cunha
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  SI: {
    code: 'SI', // Slovenia
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  SJ: {
    code: 'SJ', // Svalbard and Jan Mayen
    region: 'Europe',
    subregion: 'Nordics',
  },
  SK: {
    code: 'SK', // Slovakia
    region: 'Emerging Markets',
    subregion: 'Central Europe',
  },
  SL: {
    code: 'SL', // Sierra Leone
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  SM: {
    code: 'SM', // San Marino
    region: 'Europe',
    subregion: 'Italian Peninsula',
  },
  SN: {
    code: 'SN', // Senegal
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  SO: {
    code: 'SO', // Somalia
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  SR: {
    code: 'SR', // Suriname
    region: 'LATAM',
    subregion: 'South America',
  },
  SS: {
    code: 'SS', // South Sudan
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  ST: {
    code: 'ST', // Sao Tome and Principe
    region: 'Emerging Markets',
    subregion: 'Central Africa',
  },
  SV: {
    code: 'SV', // El Salvador
    region: 'LATAM',
    subregion: 'Central America',
  },
  SX: {
    code: 'SX', // Sint Maarten (Dutch part)
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  SY: {
    code: 'SY', // Syrian Arab Republic
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  SZ: {
    code: 'SZ', // Eswatini
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  TC: {
    code: 'TC', // Turks and Caicos Islands
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  TD: {
    code: 'TD', // Chad
    region: 'Emerging Markets',
    subregion: 'Sahel',
  },
  TF: {
    code: 'TF', // French Southern Territories
    region: 'Emerging Markets',
    subregion: 'Antarctica',
  },
  TG: {
    code: 'TG', // Togo
    region: 'Emerging Markets',
    subregion: 'West Africa',
  },
  TH: {
    code: 'TH', // Thailand
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  TJ: {
    code: 'TJ', // Tajikistan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  TK: {
    code: 'TK', // Tokelau
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  TL: {
    code: 'TL', // Timor-Leste
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  TM: {
    code: 'TM', // Turkmenistan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  TN: {
    code: 'TN', // Tunisia
    region: 'Emerging Markets',
    subregion: 'North Africa',
  },
  TO: {
    code: 'TO', // Tonga
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  TR: {
    code: 'TR', // Türkiye
    region: 'Emerging Markets',
    subregion: 'Eastern Mediterranean',
  },
  TT: {
    code: 'TT', // Trinidad and Tobago
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  TV: {
    code: 'TV', // Tuvalu
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  TW: {
    code: 'TW', // Taiwan (Province of China)
    region: 'APAC',
    subregion: 'Greater China',
  },
  TZ: {
    code: 'TZ', // Tanzania (the United Republic of)
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  UA: {
    code: 'UA', // Ukraine
    region: 'Emerging Markets',
    subregion: 'Eastern Europe',
  },
  UG: {
    code: 'UG', // Uganda
    region: 'Emerging Markets',
    subregion: 'East Africa',
  },
  UM: {
    code: 'UM', // United States Minor Outlying Islands
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  US: {
    code: 'US', // United States of America
    region: 'North America',
    subregion: 'United States',
  },
  UY: {
    code: 'UY', // Uruguay
    region: 'LATAM',
    subregion: 'South America',
  },
  UZ: {
    code: 'UZ', // Uzbekistan
    region: 'Emerging Markets',
    subregion: 'Central Asia',
  },
  VA: {
    code: 'VA', // Holy See (the)
    region: 'Europe',
    subregion: 'Italian Peninsula',
  },
  VC: {
    code: 'VC', // Saint Vincent and the Grenadines
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  VE: {
    code: 'VE', // Venezuela (Bolivarian Republic of)
    region: 'LATAM',
    subregion: 'South America',
  },
  VG: {
    code: 'VG', // Virgin Islands (British)
    region: 'LATAM',
    subregion: 'Caribbean',
  },
  VI: {
    code: 'VI', // Virgin Islands (U.S.)
    region: 'North America',
    subregion: 'United States',
  },
  VN: {
    code: 'VN', // Viet Nam
    region: 'APAC',
    subregion: 'Southeast Asia',
  },
  VU: {
    code: 'VU', // Vanuatu
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  WF: {
    code: 'WF', // Wallis and Futuna
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  WS: {
    code: 'WS', // Samoa
    region: 'APAC',
    subregion: 'Asia Pacific',
  },
  XK: {
    code: 'XK', // Kosovo
    region: 'Emerging Markets',
    subregion: 'Balkans',
  },
  YE: {
    code: 'YE', // Yemen
    region: 'Emerging Markets',
    subregion: 'Middle East',
  },
  YT: {
    code: 'YT', // Mayotte
    region: 'Emerging Markets',
    subregion: 'East African Islands',
  },
  ZA: {
    code: 'ZA', // South Africa
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  ZM: {
    code: 'ZM', // Zambia
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  ZW: {
    code: 'ZW', // Zimbabwe
    region: 'Emerging Markets',
    subregion: 'Southern Africa',
  },
  ZZ: {
    code: 'ZZ', // Unknown
    region: 'Emerging Markets',
    subregion: 'Eastern Europe',
  },
};

export type TerritoryCode = keyof typeof TERRITORIES;

// Territories we support but for which we only display the name (no flag).
// See discussion here: https://docs.google.com/document/d/1A8YUpk7q-UCvi9opyD1qWQwDconO0-vYQyKUjHVtc90/edit?disco=AAABicytHSg
export const RESTRICTED_FLAG_TERRITORIES: Partial<
  Record<TerritoryCode, string>
> = {
  AN: 'an', // Netherlands Antilles - https://en.wikipedia.org/wiki/Dissolution_of_the_Netherlands_Antilles
  CS: 'cs', // Serbia (RS) and Montenegro (ME) - Represented by their own codes respectively.
  HK: 'hk', // Hong Kong
  TW: 'tw', // Taiwan
  XK: 'xk', // Kosovo
  ZZ: 'zz', // Unknown
};

export type RestrictedTerritoryCode = keyof typeof TERRITORIES;

// Export the territories already grouped by region and sorted by region name.
export const TERRITORIES_BY_REGION = getTerritoriesByRegion(TERRITORIES);
